// src/pages/ingresos/components/LinkIntakeToPOModal.jsx
import React, { useEffect, useState } from "react";
import { supabase } from "../../../utils/supabase";
import CostCentersPanel from "./CostCentersPanel";

export default function LinkIntakeToPOModal({ open, onClose, po, onLinked }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [base, setBase] = useState("");

  // detalles de cada intake
  const [detailsByIntake, setDetailsByIntake] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [loadingDetailsId, setLoadingDetailsId] = useState(null);

  function summarizeValues(values) {
    const cleaned = [...new Set((values || []).filter(Boolean))];
    if (cleaned.length === 0) return "-";
    if (cleaned.length === 1) return cleaned[0];
    return `${cleaned[0]} (+${cleaned.length - 1})`;
  }

  useEffect(() => {
    if (!open || !po?.client_id) return;
    let active = true;

    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("intakes")
        // Solo ingresos abiertos con instrumentos
        .select(
          "id, code, reception_date, created_at, intake_items!inner(id, instruments(model, serial))"
        )
        .eq("client_id", po.client_id)
        .eq("status", "open")
        .order("created_at", { ascending: false });

      if (!error && active) {
        const filtered =
          data?.filter(
            (row) =>
              Array.isArray(row.intake_items) && row.intake_items.length > 0
          ) || [];

        setList(
          filtered.map((row) => {
            const models = row.intake_items.map((ii) => ii?.instruments?.model);
            const serials = row.intake_items.map((ii) => ii?.instruments?.serial);

            return {
              id: row.id,
              code: row.code,
              reception_date: row.reception_date,
              created_at: row.created_at,
              modelSummary: summarizeValues(models),
              serialSummary: summarizeValues(serials),
            };
          })
        );
      }
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [open, po?.client_id]);

  async function link(intakeId) {
    try {
      const baseNum = base ? Number(base) : null;
      const { data, error } = await supabase.rpc("link_intake_to_po", {
        p_intake_id: intakeId,
        p_po_id: po.id,
        p_base: baseNum,
      });
      if (error) throw error;

      // El RPC crea las OS. Guardamos en cada una la fecha elegida para este
      // ingreso, sin tocar la ficha maestra ni visitas anteriores.
      const { data: intakeItems, error: itemsError } = await supabase
        .from("intake_items")
        .select("instrument_id")
        .eq("intake_id", intakeId);
      if (itemsError) throw itemsError;

      const instrumentIds = (intakeItems || [])
        .map((row) => row.instrument_id)
        .filter(Boolean);
      const intake = list.find((row) => row.id === intakeId);
      const receptionDate =
        intake?.reception_date || String(intake?.created_at || "").slice(0, 10);
      if (instrumentIds.length && receptionDate) {
        const { error: receptionError } = await supabase
          .from("service_orders")
          .update({ reception_date: receptionDate })
          .eq("po_id", po.id)
          .in("instrument_id", instrumentIds);
        if (receptionError) throw receptionError;
      }

      onLinked?.(data);
    } catch (e) {
      alert(e.message || "No se pudo vincular el ingreso");
    }
  }

  // cargar instrumentos de un intake para vista previa
  async function toggleDetails(intakeId) {
    if (expandedId === intakeId) {
      setExpandedId(null);
      return;
    }

    if (detailsByIntake[intakeId]) {
      setExpandedId(intakeId);
      return;
    }

    try {
      setLoadingDetailsId(intakeId);
      const { data, error } = await supabase
        .from("intake_items")
        .select(
          "id, note, created_at, instruments(id, lab_id, tag, brand, model, serial)"
        )
        .eq("intake_id", intakeId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const rows =
        data?.map((r) => ({
          id: r.id,
          note: r.note || "",
          instrument: r.instruments || null,
        })) || [];

      setDetailsByIntake((prev) => ({
        ...prev,
        [intakeId]: rows,
      }));
      setExpandedId(intakeId);
    } catch (e) {
      console.error(e);
      alert(
        e.message || "No se pudieron cargar los instrumentos de este ingreso"
      );
    } finally {
      setLoadingDetailsId(null);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      {/* MODAL CONTENEDOR GRANDE */}
      <div
        className="w-full max-w-6xl max-h-[90vh] rounded-2xl bg-[#0F1115] text-white border border-white/10 shadow-2xl p-5 overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-4">
          Vincular ingreso a OC - {po?.code || ""}
        </h3>

        {/* GRID PRINCIPAL */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* ================= IZQUIERDA ================= */}
          <div className="flex flex-col">
            <div className="mb-3">
              <label className="block text-sm mb-1">
                Centro de costo (opcional)
              </label>
              <input
                className="w-32 px-3 py-2 rounded-md bg-white/10 border border-white/10 text-center"
                placeholder="5594"
                value={base}
                onChange={(e) =>
                  setBase(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))
                }
              />
              <p className="text-xs opacity-70 mt-1">
                Si lo cargas, se asigna y se resecuencian las OS.
              </p>
            </div>

            <div className="rounded-md border border-white/10 max-h-[60vh] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">Ingreso</th>
                    <th className="px-3 py-2 text-left">Modelo</th>
                    <th className="px-3 py-2 text-left">Serie</th>
                    <th className="px-3 py-2 text-right">
                      Instrumentos / Accion
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td className="px-3 py-3" colSpan={4}>
                        Cargando...
                      </td>
                    </tr>
                  ) : list.length === 0 ? (
                    <tr>
                      <td className="px-3 py-3 opacity-70" colSpan={4}>
                        No hay ingresos abiertos con instrumentos para este
                        cliente.
                      </td>
                    </tr>
                  ) : (
                    list.map((it) => (
                      <React.Fragment key={it.id}>
                        <tr className="border-t border-white/10 hover:bg-white/5">
                          <td className="px-3 py-2">{it.code}</td>
                          <td className="px-3 py-2">{it.modelSummary}</td>
                          <td className="px-3 py-2">{it.serialSummary}</td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                className="px-3 py-1.5 rounded-md border border-white/20 text-xs hover:bg-white/10"
                                onClick={() => toggleDetails(it.id)}
                              >
                                Ver instrumentos
                              </button>
                              <button
                                className="px-3 py-1.5 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs"
                                onClick={() => link(it.id)}
                              >
                                Vincular
                              </button>
                            </div>
                          </td>
                        </tr>

                        {expandedId === it.id && (
                          <tr className="border-t border-white/10">
                            <td
                              className="px-3 pb-3 pt-1 bg-black/40"
                              colSpan={4}
                            >
                              {loadingDetailsId === it.id ? (
                                <div className="text-xs opacity-70 py-2">
                                  Cargando instrumentos...
                                </div>
                              ) : (detailsByIntake[it.id] || []).length ===
                                0 ? (
                                <div className="text-xs opacity-70 py-2">
                                  Este ingreso aun no tiene instrumentos
                                  cargados.
                                </div>
                              ) : (
                                <div className="mt-1 border border-white/10 rounded-md overflow-hidden">
                                  <table className="w-full text-xs">
                                    <thead className="bg-white/5">
                                      <tr>
                                        <th className="px-2 py-1 text-left">
                                          SERV Nro.
                                        </th>
                                        <th className="px-2 py-1 text-left">
                                          Tag
                                        </th>
                                        <th className="px-2 py-1 text-left">
                                          Descripcion
                                        </th>
                                        <th className="px-2 py-1 text-left">
                                          Serie
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detailsByIntake[it.id].map((row) => {
                                        const inst = row.instrument || {};
                                        const serv =
                                          inst.lab_id != null
                                            ? String(inst.lab_id).padStart(
                                                7,
                                                "0"
                                              )
                                            : "-";
                                        const desc = [
                                          inst.brand,
                                          inst.model,
                                        ]
                                          .filter(Boolean)
                                          .join(" ");
                                        return (
                                          <tr
                                            key={row.id}
                                            className="border-t border-white/5"
                                          >
                                            <td className="px-2 py-1">
                                              {serv}
                                            </td>
                                            <td className="px-2 py-1">
                                              {inst.tag || "-"}
                                            </td>
                                            <td className="px-2 py-1">
                                              {desc || "(sin descripcion)"}
                                            </td>
                                            <td className="px-2 py-1">
                                              {inst.serial || "-"}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ================= DERECHA ================= */}
          <div className="min-h-[60vh]">
            <CostCentersPanel poId={po?.id} />
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex justify-end gap-2 mt-4">
          <button
            className="px-3 py-2 rounded-md border border-white/10"
            onClick={onClose}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
